/**
 * AppIdpAccess - Application IDP Access Management
 * Controls which applications can verify tokens from which IDPs
 */

import React, { useState, useEffect } from 'react';
import {
  listIdpConfigurations,
  getApplicationIdpAccess,
  grantIdpAccess,
  revokeIdpAccess,
} from './idpApi';

const AppIdpAccess = ({ orgId, applications }) => {
  const [selectedAppId, setSelectedAppId] = useState(null);
  const [idps, setIdps] = useState([]);
  const [appAccess, setAppAccess] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadIdps();
  }, [orgId]);

  useEffect(() => {
    if (applications && applications.length > 0 && !selectedAppId) {
      setSelectedAppId(applications[0].id);
    }
  }, [applications, selectedAppId]);

  useEffect(() => {
    if (selectedAppId) {
      loadAppAccess(selectedAppId);
    }
  }, [selectedAppId, orgId]);

  const loadIdps = async () => {
    try {
      const data = await listIdpConfigurations(orgId, true);
      setIdps(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load IDPs:', e);
      setError('Failed to load identity providers');
    } finally {
      setLoading(false);
    }
  };

  const loadAppAccess = async (appId) => {
    try {
      setLoading(true);
      const data = await getApplicationIdpAccess(orgId, appId);
      setAppAccess(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load app access:', e);
      // Might be 404 if no access configured yet
      setAppAccess([]);
    } finally {
      setLoading(false);
    }
  };

  const isIdpGranted = (idpId) => {
    return appAccess.some((access) => access.idp_config_id === idpId && access.is_active !== false);
  };

  const getAccessRecord = (idpId) => {
    return appAccess.find((access) => access.idp_config_id === idpId);
  };

  const handleToggle = async (idp) => {
    if (!selectedAppId) return;

    setSaving(idp.id);
    setError('');

    try {
      if (isIdpGranted(idp.id)) {
        await revokeIdpAccess(orgId, selectedAppId, idp.id);
        setSuccess(`Revoked access to "${idp.name}"`);
      } else {
        await grantIdpAccess(orgId, selectedAppId, idp.id);
        setSuccess(`Granted access to "${idp.name}"`);
      }
      setTimeout(() => setSuccess(''), 3000);
      await loadAppAccess(selectedAppId);
    } catch (e) {
      console.error('Failed to update access:', e);
      setError('Failed to update access');
    } finally {
      setSaving(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  if (applications.length === 0) {
    return (
      <div className="text-center p-4 bgc-grey-100 bdrs-4">
        <i className="ti-layout-grid2 fsz-xl c-grey-400 mB-10 d-block"></i>
        <p className="mB-0">No applications found. Add applications first before configuring IDP access.</p>
      </div>
    );
  }

  return (
    <div className="app-idp-access">
      <h5 className="mB-20">Application IDP Access</h5>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}

      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {success}
          <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
        </div>
      )}

      <div className="row mB-20">
        <div className="col-md-6">
          <label className="form-label fw-bold">Select Application</label>
          <select
            className="form-select"
            value={selectedAppId || ''}
            onChange={(e) => setSelectedAppId(Number(e.target.value))}
          >
            {applications.map((app) => (
              <option key={app.id} value={app.id}>
                {app.detail?.app_name || app.name || `Application ${app.id}`}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h6 className="mB-0">Authorized Identity Providers</h6>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center p-4">
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : idps.length === 0 ? (
            <div className="text-center p-4 bgc-grey-100 bdrs-4">
              <i className="ti-id-badge fsz-xl c-grey-400 mB-10 d-block"></i>
              <p className="mB-0">No identity providers configured. Add an IDP first before granting application access.</p>
            </div>
          ) : (
            <div className="list-group">
              {idps.map((idp) => {
                const granted = isIdpGranted(idp.id);
                const accessRecord = getAccessRecord(idp.id);
                const isSaving = saving === idp.id;

                return (
                  <div key={idp.id} className="list-group-item">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`idp-${idp.id}`}
                          checked={granted}
                          onChange={() => handleToggle(idp)}
                          disabled={isSaving}
                        />
                        <label className="form-check-label" htmlFor={`idp-${idp.id}`}>
                          <strong>{idp.name}</strong>
                          <p className="text-muted small mB-0">
                            Issuer: {idp.issuer_url}
                          </p>
                          {granted && accessRecord && (
                            <p className="text-success small mB-0">
                              <i className="ti-check mR-5"></i>
                              Granted: {formatDate(accessRecord.create_date)}
                            </p>
                          )}
                        </label>
                      </div>
                      {isSaving && (
                        <span className="spinner-border spinner-border-sm" role="status"></span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="alert alert-info mT-20 mB-0">
            <i className="ti-info-alt mR-10"></i>
            Applications can only verify tokens from authorized IDPs.
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppIdpAccess;
